import { useEffect } from "react";
import { useReactFlow, useStore } from "@xyflow/react";
import { CV } from "@/config/canvasSizing";
import type { PanelTreatment } from "@/store/useNodePageStore";

// Pages-panel camera. Rendered *inside* <ReactFlow> (Viewport's root is outside
// the RF context). When a node's pages panel opens, it zooms in and places the
// node's centre at the treatment's target screen point (fractions of the
// viewport) so the node isn't hidden behind the chosen panel treatment — the
// mock's computeCam(), adapted to ReactFlow's setViewport. Null-rendering.

interface Props {
  nodeId: string | null;
  treatment: PanelTreatment;
}

export default function PanelCamera({ nodeId, treatment }: Props) {
  const rf = useReactFlow();
  // Viewport pixel size from the RF store (drives the offset math + refit on resize).
  const vw = useStore((s) => s.width);
  const vh = useStore((s) => s.height);

  useEffect(() => {
    if (!nodeId || !vw || !vh) return;
    const node = rf.getNode(nodeId);
    if (!node) return;

    const w = node.width ?? node.measured?.width ?? CV.node.defaultW;
    const h = node.height ?? node.measured?.height ?? CV.node.defaultH;
    const cx = node.position.x + w / 2;
    const cy = node.position.y + h / 2;

    const { zoom, durationMs } = CV.pagesPanel.camera;
    const off = CV.pagesPanel.camera[treatment];
    const ax = vw * off.axf;
    const ay = vh * off.ayf;

    // screen = flow * zoom + translate  ⇒  translate = target - centre * zoom
    void rf.setViewport(
      { x: ax - cx * zoom, y: ay - cy * zoom, zoom },
      { duration: durationMs },
    );
  }, [nodeId, treatment, vw, vh, rf]);

  return null;
}
